import { useKeycloak } from '@react-keycloak/web';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import API from '../../services/api';
import User from '../../models/user';
import LoadingBackdrop from '../../views/MainLayout/components/shared/components/LoadingBackdrop';

let interceptorsSet = false;
let keycloakInstance = null; // Evita closures obsoletos
let refreshPromise = null; // Compartir el mismo refresh entre peticiones

const ProtectedRoute = ({ children }) => {
    const { keycloak, initialized } = useKeycloak();
    const [loading, setLoading] = useState(true);

     const refreshAccessToken = () => {
        if (!refreshPromise) {
            refreshPromise = keycloakInstance.updateToken(30)
                .then(() => {
                    if (!keycloakInstance.token) throw new Error('No se obtuvo un token válido tras el refresh');
                    return keycloakInstance.token;
                })
                .catch((err) => {
                    localStorage.removeItem('user');
                    keycloakInstance.logout({ redirectUri: window.location.origin });
                    throw err;
                })
                .finally(() => { refreshPromise = null; });
        }
        return refreshPromise;
    };

    useEffect(() => {
        if (!initialized) return;

        if (!keycloak.authenticated) {
            keycloak.login();
            return;
        }

        // Guardar referencia global
        keycloakInstance = keycloak;

        let reqInterceptor;
        let resInterceptor;

        if (keycloak?.token && !interceptorsSet) {
            reqInterceptor = API.interceptors.request.use(
                (config) => {
                    config.headers['Authorization'] = `Bearer ${keycloakInstance.token}`;
                    return config;
                },
                (error) => Promise.reject(error)
            );

            resInterceptor = API.interceptors.response.use(
                (response) => response,
                async (error) => {
                    const originalRequest = error.config;

                    if (!error.response) return Promise.reject(error);

                    if (error.response.status === 401) {
                        const retryAttempt =
                            localStorage.getItem('auth_retry_attempt');

                        if (retryAttempt) {
                            // Ya se intentó refresh -> cortar loop
                            localStorage.removeItem('auth_retry_attempt');
                            localStorage.removeItem('user');
                            keycloakInstance.logout({
                                redirectUri: window.location.origin,
                            });
                            return Promise.reject(error);
                        }

                        try {
                            localStorage.setItem('auth_retry_attempt', '1');

                            const freshToken = await refreshAccessToken();

                            originalRequest.headers['Authorization'] =
                                `Bearer ${freshToken}`;

                            const response = await API(originalRequest);

                            return response;
                        } catch (err) {
                            localStorage.removeItem('auth_retry_attempt');
                            localStorage.removeItem('user');
                            keycloakInstance.logout({
                                redirectUri: window.location.origin,
                            });
                            return Promise.reject(err);
                        }
                    }

                    return Promise.reject(error);
                },
            );
            
            interceptorsSet = true;
        }

        const fetchUser = async () => {
            try {
                if (!localStorage.getItem('user')) {
                    const user = new User(keycloak.token, keycloak.tokenParsed);
                    const res = await API.get(`/users/${user.id}`);
                    localStorage.setItem('user', JSON.stringify(res.data.data));
                }
            } catch (err) {
                console.error('Error al obtener el usuario:', err);
                keycloak.logout();
            } finally {
                setLoading(false);
            }
        };

        fetchUser();

        // Cleanup correcto: eyectar interceptors
        return () => {
            if (reqInterceptor !== undefined) API.interceptors.request.eject(reqInterceptor);
            if (resInterceptor !== undefined) API.interceptors.response.eject(resInterceptor);
            keycloakInstance = null;
            interceptorsSet = false;
            refreshPromise = null;
        };

    }, [initialized, keycloak])

    if (!initialized || loading) {
        return <LoadingBackdrop />;
    }

    return children;
};

ProtectedRoute.propTypes = {
    children: PropTypes.any.isRequired,
};

export default ProtectedRoute;
